import React, { useEffect, useMemo, useState } from 'react';

/**
 * Deterrent for Ref. Party users (mounted only for them): a faint watermark with their
 * own name + time over the whole screen (any screenshot/photo is traceable), the screen
 * is hidden while the tab/window is not focused, and print / save / PrintScreen /
 * right-click / copy are disabled. A web page cannot technically stop a phone's
 * system screenshot; the watermark is what makes a leak identifiable.
 */
export default function ScreenGuard({ user }) {
  const [now, setNow] = useState(() => new Date());
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const isField = (e) => /^(INPUT|TEXTAREA|SELECT)$/.test((e.target && e.target.tagName) || '');
    const blank = () => { try { navigator.clipboard && navigator.clipboard.writeText(' '); } catch (err) { /* ignore */ } };
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if (e.key === 'PrintScreen' || e.key === 'Snapshot') {
        blank(); setHidden(true); setTimeout(() => setHidden(false), 1500); e.preventDefault(); return;
      }
      if ((e.ctrlKey || e.metaKey) && ['p', 's', 'u'].includes(k)) e.preventDefault();
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(k)) { blank(); setHidden(true); setTimeout(() => setHidden(false), 1500); }
    };
    const onKeyUp = (e) => { if (e.key === 'PrintScreen') blank(); };
    const stop = (e) => { if (!isField(e)) e.preventDefault(); };
    const onVis = () => setHidden(document.visibilityState === 'hidden');
    const onBlur = () => setHidden(true);
    const onFocus = () => setHidden(false);
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('contextmenu', stop);
    document.addEventListener('copy', stop);
    document.addEventListener('cut', stop);
    document.addEventListener('dragstart', stop);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('contextmenu', stop);
      document.removeEventListener('copy', stop);
      document.removeEventListener('cut', stop);
      document.removeEventListener('dragstart', stop);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const text = `${user.fullName || user.email || 'User'} · ${now.toLocaleDateString('en-GB')} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  const bg = useMemo(() => {
    const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="170"><text x="20" y="100" transform="rotate(-24 160 85)" font-family="Arial" font-size="15" font-weight="700" fill="rgba(80,80,80,0.20)">${safe}</text></svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  }, [text]);

  return (
    <>
      <style>{`
        body { -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; }
        input, textarea, select { -webkit-user-select: text; user-select: text; }
        @media print { body * { display: none !important; } body::after { content: 'Printing is not allowed.'; display: block; font-size: 24px; } }
      `}</style>
      <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 99998, pointerEvents: 'none', backgroundImage: bg, backgroundRepeat: 'repeat' }} />
      {hidden && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'var(--color-surface, #fff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted, #64748b)', fontWeight: 600 }}>
          Content hidden. Return to this window to continue.
        </div>
      )}
    </>
  );
}
